import { useState, useEffect, useCallback } from "react";
import { getOutboxItems, removeOutboxItem, updateOutboxItemStatus, OutboxItem } from "../utils/offlineStorage";
import { getApiBaseUrl } from "../utils/apiConfig";

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [outboxCount, setOutboxCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  // Refresh outbox count
  const refreshOutboxCount = useCallback(async () => {
    try {
      const items = await getOutboxItems();
      const pending = items.filter((i) => i.status === "pending" || i.status === "failed");
      setOutboxCount(pending.length);
    } catch {
      setOutboxCount(0);
    }
  }, []);

  // Process outbox queue when online
  const processOutbox = useCallback(async (token: string | null) => {
    if (!navigator.onLine || !token) return;

    try {
      const items = await getOutboxItems();
      const pending = items.filter((i) => i.status === "pending" || i.status === "failed");
      if (pending.length === 0) {
        setOutboxCount(0);
        return;
      }

      setIsSyncing(true);

      for (const item of pending) {
        try {
          await updateOutboxItemStatus(item.id, "sending");

          // Compatibilidad con el endpoint: envía JSON (el servidor no recibe multipart)
          const payload = {
            from: item.from,
            to: item.to,
            cc: item.cc,
            bcc: item.bcc,
            subject: item.subject,
            text: item.body,
            html: item.bodyHtml,
            clientMessageId: item.clientMessageId,
            attachments: item.attachments?.map((att) => ({
              filename: att.name,
              contentType: att.type,
              contentBase64: att.base64,
            })),
          };

          const response = await fetch(`${getApiBaseUrl()}/mail/send`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || `HTTP ${response.status}`);
          }

          // Successfully sent: remove from outbox
          await removeOutboxItem(item.id);
        } catch (err: any) {
          console.warn(`Outbox: Failed to send item ${item.id}`, err);
          await updateOutboxItemStatus(item.id, "failed", err.message);
        }
      }

      await refreshOutboxCount();
    } finally {
      setIsSyncing(false);
    }
  }, [refreshOutboxCount]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      refreshOutboxCount();
    };

    const handleOffline = () => {
      setIsOnline(false);
      refreshOutboxCount();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    refreshOutboxCount();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refreshOutboxCount]);

  return {
    isOnline,
    outboxCount,
    isSyncing,
    refreshOutboxCount,
    processOutbox,
  };
}
